import React, { useEffect } from 'react'
import { Tabs } from '@surecompanies/core_components'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import { useSearchParams } from 'hooks/useSearchParams'
import Page from 'layouts/Page'
import { CompareDataTab } from './components/CompareDataTab'
import FetchCarrierTab from './components/FetchCarrierTab'
import FetchLogTable from './components/FetchLogTable'
import HealthPlanTab from './components/HealthPlanTab'
import { IdeonCompareTab } from './components/IdeonCompareTab'
import { PLAN_MANAGEMENT_V3 } from './paths'
import styles from './PlanManagementV3.module.scss'

const { TabPane } = Tabs

/** @internal exported for unit tests */
export const TAB_KEYS = {
  IDEON_DATA: 'ideon-data',
  FETCH_CARRIER: 'fetch-carrier',
  COMPARE_DATA: 'compare-data',
  COMPARE_IDEON: 'compare-ideon',
  HEALTH_PLAN: 'health-plan',
} as const

/** Actions that live inside the Fetch Carrier tab and therefore require it to be active. */
const FETCH_CARRIER_ACTIONS = new Set([
  'direct-download',
  'download',
  'live',
  'bot-setting',
])

/** @internal exported for unit tests */
export function resolvePlanManagementV3Tab(
  tab?: string,
  action?: string
): string {
  if (!tab && action && FETCH_CARRIER_ACTIONS.has(action)) {
    return TAB_KEYS.FETCH_CARRIER
  }
  return tab || TAB_KEYS.IDEON_DATA
}

export default function PlanManagementV3() {
  const { params: searchParams, setParams } = useSearchParams<{
    tab?: string
    action?: string
  }>()

  // When a deeplink carries an action that only the Fetch Carrier tab handles
  // (e.g. action=direct-download), force-activate that tab so the effect inside
  // FetchCarrierTab actually runs instead of silently waiting on the default tab.
  const activeTab = resolvePlanManagementV3Tab(
    searchParams.tab,
    searchParams.action
  )

  // Persist the forced tab into the URL. Auto-download / drawers / the download
  // modal all clear `action` when they finish; without `tab` the page would
  // fall back to Ideon Data and jump the user off Fetch Carrier.
  useEffect(() => {
    if (
      !searchParams.tab &&
      searchParams.action &&
      FETCH_CARRIER_ACTIONS.has(searchParams.action)
    ) {
      setParams({ tab: TAB_KEYS.FETCH_CARRIER }, { replace: true })
    }
  }, [searchParams.tab, searchParams.action, setParams])

  const handleTabChange = (key: string) =>
    setParams({ tab: key }, { replace: true })

  return (
    <Page
      title=""
      maxWidth="100%"
      breadcrumb={
        <BreadcrumbNav
          items={[
            { title: 'Plan Management V3', path: `/${PLAN_MANAGEMENT_V3}` },
          ]}
        />
      }
    >
      <div className={styles.sectionLabel}>Plan Management</div>

      <Tabs activeKey={activeTab} onChange={handleTabChange}>
        <TabPane tab="Ideon Data" key={TAB_KEYS.IDEON_DATA}>
          <FetchLogTable />
        </TabPane>
        <TabPane tab="Fetch Carrier" key={TAB_KEYS.FETCH_CARRIER}>
          <FetchCarrierTab />
        </TabPane>
        <TabPane tab="Compare Data" key={TAB_KEYS.COMPARE_DATA}>
          <CompareDataTab />
        </TabPane>
        <TabPane tab="Compare Ideon" key={TAB_KEYS.COMPARE_IDEON}>
          <IdeonCompareTab />
        </TabPane>
        <TabPane tab="Health Plan" key={TAB_KEYS.HEALTH_PLAN}>
          <HealthPlanTab />
        </TabPane>
      </Tabs>
    </Page>
  )
}
